// A broken goal is REPAIRED FORWARD (plan 101-multi-repo-goal-legs D7, slice 10 PR 8b).
//
// When a leg fails after another leg of the same plan landed, the platform reverts nothing by
// code: the repair filer (`cron.dead_dependency`) files ONE ask into `raw_tasks` naming the
// plan (`raw_tasks.extends_plan_id`), and triage decides. When it appends legs, each appended
// leg names, in `supersedes`, the failed legs it replaces, and the append
// (`@developerz/db` `appendPlanRepairLegs`) discharges them: status `superseded` with
// `superseded_by_group_id` pointing at the replacement, every edge onto a discharged leg
// re-pointed at its replacement. The plan keeps its id, so the goal stays one goal.
//
// THIS MODULE IS THE PURE HALF: the bound on repairs per plan, the one spelling of the repair
// ask's dedupe key, the refusal vocabulary, the verdict both the triage pre-check and the
// append (under the plan's row lock) run, and the audit payloads of `plan.repair_filed` and
// `plan.legs_appended`.

import { z } from 'zod';
import {
  type PlanEdge,
  PlanEdgeHold,
  PlanEdgeHoldSchema,
  PlanEdgeRelease,
  PlanEdgeReleaseSchema,
} from './plan-edge';

/**
 * The most repair asks one plan may file before the next is filed pre-settled `escalated`
 * (`plan_repair_exhausted`) for a person instead.
 *
 * THE FAILURE IT PREVENTS: a goal that cannot be reached re-repairing forever. A repair whose
 * own appended legs end unsatisfied ends the plan again, and the filer files the next repair
 * for it; nothing else stops that loop — each round mints fresh legs, so no dedupe key
 * repeats and no task-level bound sees the chain. The reporter's goal stays "in progress"
 * indefinitely and no maintainer is pulled in. At the cap a person reads it once.
 *
 * THREE, for the same reason as `RAW_TASK_INFO_ROUNDS_MAX` and NOT that constant: a forward
 * fix, a fix of the fix, and the misunderstanding the second reveals. A goal still broken
 * after three repairs is one a maintainer has to look at. Not a cost cap: each round is one
 * triage share plus the legs it chose to run (`docs/standards/agent-budgets.md`).
 */
export const PLAN_REPAIR_ATTEMPTS_MAX = 3;

/**
 * How recently a plan's goal must have BROKEN for the filer to repair it unprompted: the
 * newest failed leg's task changed within this window (`@developerz/db`
 * `listPlansAwaitingRepair`, on the DB clock).
 *
 * THE FAILURE IT PREVENTS: a goal that broke days ago lives in a world that has moved on —
 * the repos merged other work, the reporter worked around it, the owner forgot it. Repairing
 * it unprompted opens pull requests on customer repos for a stale goal and surprises the
 * owner. A person can still re-file it. Not a cost cap: inside the window every broken goal
 * is repaired whatever it costs (`docs/standards/agent-budgets.md`).
 *
 * 72 hours: a weekend plus a working day, so a goal that broke on a Friday is still repaired
 * on Monday, and a filing that keeps failing is retried on every tick for three days while it
 * pages (`plan_repair_file_failed`).
 */
export const PLAN_REPAIR_LOOKBACK_MS = 72 * 60 * 60_000;

/**
 * The `escalate` reason of a repair ask filed past {@link PLAN_REPAIR_ATTEMPTS_MAX}: the
 * admission drain settles such an ask `escalate` / this reason at its first claim, before any
 * judge, because a draft cannot carry a status. An ordinary `escalate` `reason`
 * (`VerdictReasonSchema`, a bounded string, not a stored closed set). STORED on the verdict.
 */
export const PLAN_REPAIR_EXHAUSTED_REASON = 'plan_repair_exhausted' as const;

/**
 * May the filer file one more ordinary repair ask for a plan that has already had
 * `priorAttempts` of them? `false` means the next one is filed pre-settled `escalated`.
 */
export function planRepairMayFile(priorAttempts: number): boolean {
  if (!Number.isInteger(priorAttempts) || priorAttempts < 0) {
    throw new Error(
      `planRepairMayFile: priorAttempts must be a non-negative integer, got ${String(priorAttempts)}`,
    );
  }
  return priorAttempts < PLAN_REPAIR_ATTEMPTS_MAX;
}

/**
 * The first segment of {@link planRepairDedupeKey}, for a reader that matches the key in SQL
 * (`@developerz/db` `listPlansAwaitingRepair`) instead of re-spelling it. STORED (the key).
 */
export const PLAN_REPAIR_DEDUPE_KEY_PREFIX = 'plan_repair' as const;

/**
 * The repair ask's `raw_tasks.dedupe_key`: `plan_repair:<planId>:<groupId>:<attempt>`, the
 * failed leg the round answers and the round's 1-based number. A second tick over the same
 * ended plan files nothing (the `(account_id, dedupe_key)` unique index), while the NEXT round
 * — a repair whose own legs ended — earns a new key. STORED: changing the spelling re-files
 * every repair the old spelling already filed.
 */
export function planRepairDedupeKey(planId: string, groupId: string, attempt: number): string {
  if (planId === '' || groupId === '' || !Number.isInteger(attempt) || attempt < 1) {
    throw new Error(
      `planRepairDedupeKey: needs a plan id, a group id and a 1-based attempt; got ` +
        `(${JSON.stringify(planId)}, ${JSON.stringify(groupId)}, ${String(attempt)})`,
    );
  }
  return `${PLAN_REPAIR_DEDUPE_KEY_PREFIX}:${planId}:${groupId}:${String(attempt)}`;
}

/**
 * Where one existing group of the plan stands, as the repair reads it. Derived in SQL by the
 * append (`@developerz/db` `readPlanRepairState`), from the same fragments the done and ended
 * verdicts read:
 *   - `satisfied`  — `planGroupSatisfiedSql`: merged, or delivered without a PR;
 *   - `cancelled`  — the group row is `cancelled` (a person stopped it);
 *   - `superseded` — an earlier repair discharged it;
 *   - `failed`     — its task ended (terminal, not superseded) and none of the above: the leg
 *                    a repair MUST answer, or the plan can never be `done`;
 *   - `live`       — anything else: work in flight, or a group that can still dispatch.
 */
export const PlanRepairGroupState = {
  Satisfied: 'satisfied',
  Cancelled: 'cancelled',
  Superseded: 'superseded',
  Failed: 'failed',
  Live: 'live',
} as const;
export const PlanRepairGroupStateSchema = z.enum(PlanRepairGroupState);
export type PlanRepairGroupState = z.infer<typeof PlanRepairGroupStateSchema>;

/** One existing group: its state and, when `superseded`, the group that replaced it. */
export type PlanRepairGroupFact = {
  groupId: string;
  state: PlanRepairGroupState;
  supersededBy: string | null;
};

/**
 * One existing edge: `groupId` waits on `upstream`, with its stored kind (`PlanEdge`, D2). The
 * kind matters when the re-point lands two edges of one dependent on one replacement
 * ({@link strictestPlanEdgeKind}).
 */
export type PlanRepairEdgeFact = { groupId: string } & PlanEdge;

/** What an edge waits for, without the upstream it waits on (`release`, `environment`, `hold`). */
export type PlanEdgeKind = Omit<PlanEdge, 'upstream'>;

/**
 * The ONE edge kind that keeps both of two requirements on one upstream, or `null` when no
 * single stored edge can (plan 101 D7: a repair that replaces two failed legs with one leg
 * lands a dependent's two edges on one `(group, upstream)` pair, which holds one row).
 *
 * Strictness, per field, never loosening either input:
 *   - `release`: `deployed` to an environment ⊒ `merged` (a deploy implies the merge). Two
 *     `deployed` edges agree only on the SAME environment; two environments are INCOMPARABLE
 *     (one row names one environment), so the answer is `null`.
 *   - `hold`: `start` ⊒ `merge` (`start` keeps the dependent unclaimed until the release;
 *     `merge` lets it run and holds only its merge).
 * The two fields combine independently, so `merged/start` with `deployed@prod/merge` answers
 * `deployed@prod/start`: stricter than either input (the dependent now waits to START for the
 * deploy), never looser than one of them.
 */
export function strictestPlanEdgeKind(a: PlanEdgeKind, b: PlanEdgeKind): PlanEdgeKind | null {
  const hold =
    a.hold === PlanEdgeHold.Start || b.hold === PlanEdgeHold.Start
      ? PlanEdgeHold.Start
      : PlanEdgeHold.Merge;
  const aDeployed = a.release === PlanEdgeRelease.Deployed;
  const bDeployed = b.release === PlanEdgeRelease.Deployed;
  if (aDeployed && bDeployed && a.environment !== b.environment) return null;
  if (aDeployed) return { release: PlanEdgeRelease.Deployed, environment: a.environment, hold };
  if (bDeployed) return { release: PlanEdgeRelease.Deployed, environment: b.environment, hold };
  return { release: PlanEdgeRelease.Merged, environment: null, hold };
}

/** One dependent's edges onto discharged legs that the re-point lands on ONE replacement. */
export type PlanRepairCoalescedEdge = PlanEdgeKind & {
  /** The dependent. */
  groupId: string;
  /** The discharged upstreams whose edges become this one row. */
  from: string[];
  /** The replacement every one of them pointed at. */
  to: string;
};

/** One appended leg as the verdict reads it: its id, the ids it waits on, what it replaces. */
export type PlanRepairLeg = {
  groupId: string;
  dependsOn: readonly string[];
  supersedes: readonly string[];
};

/**
 * Why a repair append was refused. CLOSED: these land in the triage refusal path's audit rows
 * and in `raw_tasks.verdict`. `packages/agent`'s `TriageRefusal` spreads this object, as it
 * spreads `EdgeRefusal`.
 */
export const PlanRepairRefusal = {
  /** No plan with that id on the ask's account (deleted, or another tenant's). */
  PlanNotFound: 'plan_not_found',
  /** The plan is `done` or `cancelled`: a closed goal is not reopened by a repair. */
  PlanNotRepairable: 'plan_not_repairable',
  /** The append names no leg. */
  RepairNamesNoLeg: 'repair_names_no_leg',
  /** An appended leg id the plan already holds, or one named twice in the append. */
  GroupIdTaken: 'group_id_taken',
  /**
   * A FAILED group (see {@link PlanRepairGroupState}) no appended leg's `supersedes` names.
   * Left alone it keeps the plan's `done` unreachable forever.
   */
  RepairLeavesFailedLeg: 'repair_leaves_failed_leg',
  /** A `supersedes` naming a SATISFIED group: discharging delivered work would hide it. */
  SupersedeTargetSatisfied: 'supersede_target_satisfied',
  /** A `supersedes` naming a group a person CANCELLED: a repair does not overrule a stop. */
  SupersedeTargetCancelled: 'supersede_target_cancelled',
  /**
   * A `supersedes` naming a group an earlier repair already discharged: the leg to answer is
   * its replacement, which the pointer names.
   */
  SupersedeTargetAlreadySuperseded: 'supersede_target_already_superseded',
  /** A `supersedes` naming a group still in flight: it has not failed. */
  SupersedeTargetLive: 'supersede_target_live',
  /** A `supersedes` naming no group of the plan (an appended leg included). */
  SupersedeTargetUnknown: 'supersede_target_unknown',
  /** One group named by two `supersedes`: its pointer can name one replacement. */
  SupersedeTargetNamedTwice: 'supersede_target_named_twice',
  /**
   * An appended leg waiting on a group being discharged or already discharged: the re-point
   * would turn that edge into a wait on itself or on dead work. Wait on the replacement.
   */
  LegDependsOnSuperseded: 'leg_depends_on_superseded',
  /** An appended leg waiting on an id that is neither a group of the plan nor another leg. */
  LegDependsOnUnknown: 'leg_depends_on_unknown',
  /** The plan's edges after the re-point, plus the appended legs' own, hold a cycle. */
  RepairCreatesCycle: 'repair_creates_cycle',
  /**
   * The re-point lands two edges of one dependent on one replacement and no single edge keeps
   * both requirements (`deployed` to two different environments, {@link
   * strictestPlanEdgeKind}): keeping either would silently drop the other's gate. `groupIds`
   * names each such dependent, then the discharged upstreams its edges waited on.
   */
  RepairMergesIncompatibleEdges: 'repair_merges_incompatible_edges',
} as const;
export type PlanRepairRefusal = (typeof PlanRepairRefusal)[keyof typeof PlanRepairRefusal];

/**
 * A refusal with the values that failed. `groupIds` names the offending ids per code: the
 * unanswered failed groups, the bad `supersedes` targets, the legs whose dependency failed
 * (`leg_depends_on_*`), the ids on the cycle.
 */
export type PlanRepairRefusalDetail =
  | { code: typeof PlanRepairRefusal.PlanNotFound }
  | { code: typeof PlanRepairRefusal.PlanNotRepairable; planStatus: string }
  | { code: typeof PlanRepairRefusal.RepairNamesNoLeg }
  | {
      code: Exclude<
        PlanRepairRefusal,
        | typeof PlanRepairRefusal.PlanNotFound
        | typeof PlanRepairRefusal.PlanNotRepairable
        | typeof PlanRepairRefusal.RepairNamesNoLeg
      >;
      groupIds: string[];
    };

/** The pointers an accepted repair writes: one hop each, chains already compressed. */
export type PlanRepairVerdict =
  | {
      kind: 'ok';
      /** Each named target, flipped `superseded` and pointed at the leg naming it. */
      supersede: Array<{ groupId: string; by: string }>;
      /** Each group already superseded BY a target, re-pointed at the target's replacement. */
      compress: Array<{ groupId: string; by: string }>;
      /** Each dependent's edges the re-point lands on one replacement, as the ONE row kept. */
      coalesce: PlanRepairCoalescedEdge[];
    }
  | { kind: 'refused'; refusal: PlanRepairRefusalDetail };

const refused = (refusal: PlanRepairRefusalDetail): PlanRepairVerdict => ({
  kind: 'refused',
  refusal,
});

/** Ids that occur more than once in `ids`, each once, in first-repeat order. */
function repeated(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const out = new Set<string>();
  for (const id of ids) (seen.has(id) ? out : seen).add(id);
  return [...out];
}

/** The `supersedes` checks: every target named once, and each a FAILED group of the plan. */
function targetRefusal(
  byId: ReadonlyMap<string, PlanRepairGroupFact>,
  targets: readonly string[],
): PlanRepairRefusalDetail | null {
  const twice = repeated(targets);
  if (twice.length > 0)
    return { code: PlanRepairRefusal.SupersedeTargetNamedTwice, groupIds: twice };
  const inState = (s: PlanRepairGroupState | undefined) =>
    targets.filter((id) => byId.get(id)?.state === s);
  const ordered: Array<[PlanRepairRefusalDetail['code'], string[]]> = [
    [PlanRepairRefusal.SupersedeTargetUnknown, targets.filter((id) => !byId.has(id))],
    [PlanRepairRefusal.SupersedeTargetSatisfied, inState(PlanRepairGroupState.Satisfied)],
    [PlanRepairRefusal.SupersedeTargetCancelled, inState(PlanRepairGroupState.Cancelled)],
    [PlanRepairRefusal.SupersedeTargetAlreadySuperseded, inState(PlanRepairGroupState.Superseded)],
    [PlanRepairRefusal.SupersedeTargetLive, inState(PlanRepairGroupState.Live)],
  ];
  for (const [code, groupIds] of ordered) {
    if (groupIds.length > 0) return { code, groupIds } as PlanRepairRefusalDetail;
  }
  return null;
}

/** The appended legs' own dependencies: on a live, known, undischarged group or leg. */
function dependencyRefusal(
  byId: ReadonlyMap<string, PlanRepairGroupFact>,
  legs: readonly PlanRepairLeg[],
  targets: ReadonlySet<string>,
): PlanRepairRefusalDetail | null {
  const legIds = new Set(legs.map((l) => l.groupId));
  const onDischarged = legs.filter((l) =>
    l.dependsOn.some(
      (d) => targets.has(d) || byId.get(d)?.state === PlanRepairGroupState.Superseded,
    ),
  );
  if (onDischarged.length > 0) {
    return {
      code: PlanRepairRefusal.LegDependsOnSuperseded,
      groupIds: onDischarged.map((l) => l.groupId),
    };
  }
  const onUnknown = legs.filter((l) => l.dependsOn.some((d) => !byId.has(d) && !legIds.has(d)));
  if (onUnknown.length > 0) {
    return {
      code: PlanRepairRefusal.LegDependsOnUnknown,
      groupIds: onUnknown.map((l) => l.groupId),
    };
  }
  return null;
}

/**
 * The first cycle in the graph the plan would hold after the append: every existing edge of
 * an undischarged dependent with its upstream re-pointed through `by`, plus each leg's own.
 * `null` when acyclic. A discharged group's own edges are left out: it never runs again.
 */
function cycleAfter(
  byId: ReadonlyMap<string, PlanRepairGroupFact>,
  edges: readonly PlanRepairEdgeFact[],
  legs: readonly PlanRepairLeg[],
  by: ReadonlyMap<string, string>,
): string[] | null {
  const next = new Map<string, string[]>();
  const add = (from: string, to: string) => next.set(from, [...(next.get(from) ?? []), to]);
  for (const e of edges) {
    if (by.has(e.groupId) || byId.get(e.groupId)?.state === PlanRepairGroupState.Superseded)
      continue;
    add(e.groupId, by.get(e.upstream) ?? e.upstream);
  }
  for (const l of legs) for (const d of l.dependsOn) add(l.groupId, d);
  const done = new Set<string>();
  const path: string[] = [];
  const onPath = new Set<string>();
  const visit = (id: string): string[] | null => {
    if (onPath.has(id)) return path.slice(path.indexOf(id));
    if (done.has(id)) return null;
    onPath.add(id);
    path.push(id);
    for (const up of next.get(id) ?? []) {
      const found = visit(up);
      if (found !== null) return found;
    }
    path.pop();
    onPath.delete(id);
    done.add(id);
    return null;
  };
  for (const id of next.keys()) {
    const found = visit(id);
    if (found !== null) return found;
  }
  return null;
}

/**
 * The re-point's merges: each dependent whose edges onto two or more discharged legs land on
 * ONE replacement, folded through {@link strictestPlanEdgeKind} into the one row kept. A fold
 * with no answer is a conflict, named as its dependent then those upstreams. Every dependent
 * counts, a discharged one too: its edges are re-pointed like any other, so the row it keeps
 * must be honest even though it never runs again. Ordered by dependent, then replacement.
 */
function coalescedAfter(
  edges: readonly PlanRepairEdgeFact[],
  by: ReadonlyMap<string, string>,
): { coalesce: PlanRepairCoalescedEdge[]; conflicts: string[] } {
  const buckets = new Map<string, { groupId: string; to: string; edges: PlanRepairEdgeFact[] }>();
  for (const e of edges) {
    const to = by.get(e.upstream);
    if (to === undefined) continue;
    const key = `${e.groupId}\u0000${to}`;
    const bucket = buckets.get(key) ?? { groupId: e.groupId, to, edges: [] };
    bucket.edges.push(e);
    buckets.set(key, bucket);
  }
  const coalesce: PlanRepairCoalescedEdge[] = [];
  const conflicts: string[] = [];
  for (const { groupId, to, edges: list } of buckets.values()) {
    if (list.length < 2) continue;
    let kind: PlanEdgeKind | null = {
      release: PlanEdgeRelease.Merged,
      environment: null,
      hold: PlanEdgeHold.Merge,
    };
    for (const e of list) kind = kind === null ? null : strictestPlanEdgeKind(kind, e);
    const from = list.map((e) => e.upstream);
    if (kind === null) conflicts.push(groupId, ...from);
    else coalesce.push({ groupId, from, to, ...kind });
  }
  coalesce.sort((a, b) =>
    a.groupId === b.groupId ? a.to.localeCompare(b.to) : a.groupId.localeCompare(b.groupId),
  );
  return { coalesce, conflicts: [...new Set(conflicts)] };
}

/**
 * Decide a repair append. Pure: the same facts answer the same way, so triage can run it
 * before minting a leg's task and the append re-runs it under the plan's row lock on the
 * facts it just read. The refusal order is fixed (the first failing check answers): no leg,
 * a taken id, the `supersedes` targets, an unanswered failed leg, the legs' dependencies, two
 * edges the re-point would merge with no single kind keeping both, a cycle.
 */
export function planRepairVerdict(
  facts: { groups: readonly PlanRepairGroupFact[]; edges: readonly PlanRepairEdgeFact[] },
  legs: readonly PlanRepairLeg[],
): PlanRepairVerdict {
  if (legs.length === 0) return refused({ code: PlanRepairRefusal.RepairNamesNoLeg });
  const byId = new Map(facts.groups.map((g) => [g.groupId, g]));
  const legIds = legs.map((l) => l.groupId);
  const taken = [...new Set([...legIds.filter((id) => byId.has(id)), ...repeated(legIds)])];
  if (taken.length > 0) return refused({ code: PlanRepairRefusal.GroupIdTaken, groupIds: taken });
  const targets = legs.flatMap((l) => l.supersedes);
  const bad = targetRefusal(byId, targets);
  if (bad !== null) return refused(bad);
  const named = new Set(targets);
  const unanswered = facts.groups
    .filter((g) => g.state === PlanRepairGroupState.Failed && !named.has(g.groupId))
    .map((g) => g.groupId);
  if (unanswered.length > 0) {
    return refused({ code: PlanRepairRefusal.RepairLeavesFailedLeg, groupIds: unanswered });
  }
  const badDep = dependencyRefusal(byId, legs, named);
  if (badDep !== null) return refused(badDep);
  const by = new Map(legs.flatMap((l) => l.supersedes.map((t) => [t, l.groupId] as const)));
  const { coalesce, conflicts } = coalescedAfter(facts.edges, by);
  if (conflicts.length > 0) {
    return refused({ code: PlanRepairRefusal.RepairMergesIncompatibleEdges, groupIds: conflicts });
  }
  const cycle = cycleAfter(byId, facts.edges, legs, by);
  if (cycle !== null)
    return refused({ code: PlanRepairRefusal.RepairCreatesCycle, groupIds: cycle });
  const compress = facts.groups.flatMap((g) => {
    const to = g.supersededBy === null ? undefined : by.get(g.supersededBy);
    return to === undefined ? [] : [{ groupId: g.groupId, by: to }];
  });
  return {
    kind: 'ok',
    supersede: [...by].map(([groupId, to]) => ({ groupId, by: to })),
    compress,
    coalesce,
  };
}

// ─── audit payloads ───────────────────────────────────────────────────────────

const PointerSchema = z.strictObject({ groupId: z.string().min(1), by: z.string().min(1) });

/**
 * `plan.legs_appended` `input` — one row per accepted repair append, written in the append's
 * own transaction (`@developerz/db` `appendPlanRepairLegs`'s REQUIRED `record`). Addresses and
 * edges only, never a title or an envelope (the `plan.recorded` posture).
 */
export const PlanLegsAppendedInputSchema = z.strictObject({
  planId: z.string().min(1),
  accountId: z.string().min(1),
  /** The repair ask whose triage appended these legs. */
  rawTaskId: z.string().min(1),
  /** The appended legs, in append order. */
  legs: z.array(
    z.strictObject({
      groupId: z.string().min(1),
      repoFullName: z.string().min(1),
      taskId: z.string().min(1).nullable(),
      dependsOn: z.array(z.string().min(1)),
      supersedes: z.array(z.string().min(1)),
    }),
  ),
  /** Each discharged group and its replacement. */
  superseded: z.array(PointerSchema),
  /** Each older pointer moved onto a new replacement (chain compression). */
  compressed: z.array(PointerSchema),
  /** Each edge whose upstream moved from a discharged group to its replacement, kind kept. */
  repointedEdges: z.array(
    z.strictObject({
      groupId: z.string().min(1),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  ),
  /**
   * Each dependent whose edges onto several discharged legs landed on ONE replacement: those
   * rows became one, of the strictest kind that keeps every requirement
   * ({@link strictestPlanEdgeKind}); none is dropped. Not in `repointedEdges`.
   */
  coalescedEdges: z.array(
    z.strictObject({
      groupId: z.string().min(1),
      from: z.array(z.string().min(1)).min(2),
      to: z.string().min(1),
      release: PlanEdgeReleaseSchema,
      environment: z.string().min(1).nullable(),
      hold: PlanEdgeHoldSchema,
    }),
  ),
  /** The plan went `ended -> open` in this transaction. */
  reopened: z.boolean(),
});
export type PlanLegsAppendedInput = z.infer<typeof PlanLegsAppendedInputSchema>;

/**
 * `plan.repair_filed` `input` — the repair filer filed (or, past {@link
 * PLAN_REPAIR_ATTEMPTS_MAX}, pre-settled `escalated`) one repair ask for an ended plan.
 */
export const PlanRepairFiledInputSchema = z.strictObject({
  planId: z.string().min(1),
  accountId: z.string().min(1),
  rawTaskId: z.string().min(1),
  /** {@link planRepairDedupeKey}'s answer for this round. */
  dedupeKey: z.string().min(1),
  /** 1-based round of this plan's repairs. */
  attempt: z.number().int().min(1),
  /** The failed legs the ask names (every group a repair must answer). */
  failedGroupIds: z.array(z.string().min(1)).min(1),
  /** `true` when the bound was spent and the ask was filed pre-settled `escalated`. */
  escalated: z.boolean(),
});
export type PlanRepairFiledInput = z.infer<typeof PlanRepairFiledInputSchema>;
